import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import type { Entry } from '../api/entries'
import { createEntry, getEntry } from '../api/entries'
import { getUserProfile, followUser, unfollowUser, updateIdentity } from '../api/users'
import { AvatarImage } from '../components/AvatarImage'
import { MarkdownRenderer } from '../components/Entry/MarkdownRenderer'
import { useAuthStore } from '../store/auth'
import { useUsername } from '../hooks/useUsername'
import { usePageHeader } from '../hooks/usePageHeader'
import { useAuthorTheme } from '../hooks/useAuthorTheme'

type FollowStatus = 'following' | 'pending' | 'not_following' | null

interface IdentityCard {
  user_id: string
  username: string
  bio_entry_id: string | null
  avatar_entry_id: string | null
  color_theme: Record<string, string> | null
}

export function Profile() {
  const username = useUsername()
  const currentUsername = useAuthStore((s) => s.username)
  const token = useAuthStore((s) => s.token)
  const navigate = useNavigate()
  const [identity, setIdentity] = useState<IdentityCard | null>(null)
  const [profileVisibility, setProfileVisibility] = useState<number | null>(null)
  const [followStatus, setFollowStatus] = useState<FollowStatus>(null)
  const [bioEntry, setBioEntry] = useState<Entry | null>(null)
  const [error, setError] = useState('')
  const [followLoading, setFollowLoading] = useState(false)
  const [followError, setFollowError] = useState('')
  const [creatingBio, setCreatingBio] = useState(false)

  const isSelf = !!token && username === currentUsername
  useAuthorTheme(username ?? undefined)

  useEffect(() => {
    if (!username) return
    // Identity (avatar/bio) is only available to approved followers; a 404 here is not fatal.
    api.get<IdentityCard>(`/users/${username}/identity`)
      .then((card) => {
        setIdentity(card)
        if (card.bio_entry_id) {
          getEntry(card.bio_entry_id).then(setBioEntry).catch(() => null)
        }
      })
      .catch(() => null)
    getUserProfile(username)
      .then((p) => {
        setProfileVisibility(p.visibility)
        setFollowStatus(p.follow_status)
      })
      .catch((e) => setError(e.message))
  }, [username])

  async function handleCreateBioEntry() {
    if (!username) return
    setCreatingBio(true)
    try {
      const entry = await createEntry({ name: 'profile', content: '', privacy: 3 })
      await updateIdentity(username, { bio_entry_id: entry.id })
      navigate(`/~${username}/entry/${entry.id}`)
    } catch {
      setCreatingBio(false)
    }
  }

  async function handleFollow() {
    if (!username) return
    setFollowLoading(true)
    setFollowError('')
    try {
      const result = await followUser(username)
      setFollowStatus(result.status as FollowStatus)
    } catch (e) {
      setFollowError(e instanceof Error ? e.message : 'Failed')
    } finally {
      setFollowLoading(false)
    }
  }

  async function handleUnfollow() {
    if (!username) return
    setFollowLoading(true)
    setFollowError('')
    try {
      await unfollowUser(username)
      setFollowStatus('not_following')
    } catch (e) {
      setFollowError(e instanceof Error ? e.message : 'Failed')
    } finally {
      setFollowLoading(false)
    }
  }

  const headerActions = useMemo(() => username ? (
    <span style={{ display: 'flex', gap: '0.75rem', fontSize: '0.85rem' }}>
      <Link to={`/~${username}/journal`}>journal</Link>
      <Link to={`/~${username}/pins`}>pins</Link>
      <Link to={`/~${username}/tags`}>tags</Link>
      <a href={`/~${username}/feed.atom`}>atom feed</a>
    </span>
  ) : null, [username])

  usePageHeader(username ? `~${username}` : '', headerActions)

  if (error) return <p style={{ color: 'var(--color-1)' }}>{error}</p>

  const ct = identity?.color_theme ?? bioEntry?.color_theme ?? null

  const canFollow = !isSelf && !!token && profileVisibility !== null && profileVisibility !== 0

  function renderFollowButton() {
    if (!canFollow) return null
    let label: string
    let onClick: () => void
    if (followStatus === 'following') {
      label = followLoading ? '…' : 'unfollow'
      onClick = handleUnfollow
    } else if (followStatus === 'pending') {
      label = followLoading ? '…' : 'request pending'
      onClick = handleUnfollow
    } else if (followStatus === 'not_following') {
      label = followLoading ? '…' : 'follow'
      onClick = handleFollow
    } else {
      return null
    }
    return (
      <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button
          type="button"
          onClick={onClick}
          disabled={followLoading}
          style={{ fontSize: '0.85rem', opacity: followStatus === 'pending' ? 0.7 : 1 }}
        >
          {label}
        </button>
        {followError && <span style={{ fontSize: '0.8rem', color: 'var(--color-1)' }}>{followError}</span>}
      </div>
    )
  }

  // Stub view for ask-to-follow accounts the viewer hasn't been approved for yet.
  const isStub = profileVisibility === 2 && (followStatus === 'not_following' || followStatus === 'pending')

  if (isStub) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--color-fg)', opacity: 0.1 }} />
          {renderFollowButton()}
        </div>
        <p style={{ opacity: 0.5, fontSize: '0.9rem' }}>this account requires approval to follow</p>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        {identity?.avatar_entry_id && (
          <AvatarImage entryId={identity.avatar_entry_id} size={80} />
        )}
        {renderFollowButton()}
      </div>

      {identity?.bio_entry_id && bioEntry?.content && (
        <div style={{ margin: '1rem 0' }}>
          <MarkdownRenderer
            entryId={bioEntry.id}
            authorUsername={username ?? ''}
            content={bioEntry.content}
            colorTheme={ct}
          />
          {isSelf && (
            <Link
              to={`/~${username}/entry/${identity.bio_entry_id}`}
              style={{ fontSize: '0.8rem', opacity: 0.5 }}
            >
              edit
            </Link>
          )}
        </div>
      )}

      {isSelf && !identity?.bio_entry_id && (
        <p style={{ fontSize: '0.9rem', margin: '1rem 0' }}>
          <button
            type="button"
            onClick={handleCreateBioEntry}
            disabled={creatingBio}
            style={{ fontSize: '0.85rem' }}
          >
            {creatingBio ? 'creating…' : 'create profile entry'}
          </button>
        </p>
      )}

    </div>
  )
}
